/**
 * String Helper Utilities
 * Collection of string manipulation and generation functions
 */

/**
 * Generates a unique UUID v4 for idempotency keys and unique identifiers
 * @returns {string} A unique UUID string in the format xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * @example
 * const uniqueId = stringHelper.generateUUID();
 * // Returns: "a1b2c3d4-e5f6-4789-a012-b3c4d5e6f7g8"
 */
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
};

/**
 * Gets the current session UUID or generates a new one if expired
 * Session UUID expires after 2 hours
 * @param {Object} storageHelper - Storage helper instance for cookie management
 * @returns {string} Current or new session UUID
 */
const getOrGenerateSessionUUID = (storageHelper) => {
    const currentUUID = storageHelper.getItem("session_uuid");
    const uuidTimestamp = storageHelper.getItem("session_uuid_timestamp");

    // Check if UUID exists and is still valid (less than 2 hours old)
    if (currentUUID && uuidTimestamp) {
        const timestampDate = new Date(uuidTimestamp);
        const now = new Date();
        const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        const timeDiff = now - timestampDate;

        // If less than 2 hours, return existing UUID
        if (timeDiff < twoHoursInMs) {
            return currentUUID;
        }
    }

    // Generate new UUID if expired or doesn't exist
    const newUUID = generateUUID();
    const newTimestamp = new Date().toISOString();

    // Store new UUID and timestamp (expires in 2 hours)
    const twoHoursDays = 2 / 24; // Convert 2 hours to days for cookie expiration
    storageHelper.setItem("session_uuid", newUUID, twoHoursDays);
    storageHelper.setItem("session_uuid_timestamp", newTimestamp, twoHoursDays);

    return newUUID;
};

/**
 * Sets a new session UUID with current timestamp
 * @param {Object} storageHelper - Storage helper instance for cookie management
 * @param {string|null} expireOn - Optional expiration date for the session
 * @returns {string} The newly generated UUID
 */
const setSessionUUID = (storageHelper, expireOn = null) => {
    const newUUID = generateUUID();
    const newTimestamp = new Date().toISOString();

    if (expireOn) {
        // Use provided expiration date
        storageHelper.setItem("session_uuid", newUUID, undefined, expireOn);
        storageHelper.setItem("session_uuid_timestamp", newTimestamp, undefined, expireOn);
    } else {
        // Default to 2 hours
        const twoHoursDays = 2 / 24;
        storageHelper.setItem("session_uuid", newUUID, twoHoursDays);
        storageHelper.setItem("session_uuid_timestamp", newTimestamp, twoHoursDays);
    }

    return newUUID;
};
const convertReactStringToHtml = (content) => {
    if (!content || typeof content !== 'string') return "";

    let html = content
      // Remove React Fragments
      .replace(/<>/g, "")
      .replace(/<\/>/g, "")
      // Convert style={{...}} to style="..."
      .replace(/style={{([^}]*)}}/g, (match, styles) => {
        const styleStr = styles.split(',').map(s => {
          const parts = s.split(':');
          if (parts.length < 2) return '';
          const key = parts[0].trim().replace(/([A-Z])/g, "-$1").toLowerCase();
          const value = parts.slice(1).join(':').trim().replace(/'/g, "").replace(/"/g, "");
          return `${key}:${value}`;
        }).join(';');
        return `style="${styleStr}"`;
      })
      // Convert className to class
      .replace(/className=/g, "class=")
      // Convert Markdown bold (**text**) to <strong>text</strong>
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    return html;
  };

/**
 * String Helper Object
 * Exports all string utility functions
 */
export const stringHelper = {
    generateUUID,
    getOrGenerateSessionUUID,
    setSessionUUID,
    convertReactStringToHtml,
};

export default stringHelper;
